<template lang="pug">
.race(ref="rootRef" :class="{ 'is-static': isStatic }" role="img" :aria-label="description")
  .race-stage(ref="stageRef" :style="{ height: stageHeight }")
    canvas.race-canvas(ref="canvasRef" aria-hidden="true")
    .race-lane(v-for="(lane, index) in laneViews" :key="lane.id" :class="`is-${lane.id}`" :style="{ top: `${(index / laneViews.length) * 100}%` }" aria-hidden="true")
      .race-lane-head
        span.race-lane-name {{ lane.name }}
        span.race-lane-rate {{ lane.rateText }}
      .race-lane-total
        span.race-lane-gb {{ lane.gbText }}
        span.race-lane-unit GB
    //- While the run holds at the end, the unfilled part of each slower lane says how many times faster PandaCore was.
    .race-lane-mult(v-for="lane in laneViews.filter(item => item.multiplierText)" :key="`m${lane.id}`" :class="{ 'is-shown': holding }" :style="{ left: `clamp(0px, calc(${lane.fillRatio * 100}% + 12px), calc(100% - 7rem))`, top: `${lane.centerY}px` }" aria-hidden="true")
      strong {{ lane.multiplierText }}
  .race-axis(aria-hidden="true")
    span.race-tick(v-for="tick in ticks" :key="tick" :style="{ left: `${(tick / periodSeconds) * 100}%` }") {{ tick }} s
    .race-playhead(ref="playheadRef")
</template>

<script setup lang="ts">
import { formatMultiplier, gigabytesTransferred, verdictFromMedians } from '~/utils/pandaCoreBenchmarks'

const props = withDefaults(defineProps<{
  /** Measured sustained rate, Gbit/s. */
  pandaCore: number
  singBox: number
  /** Optional third lane, e.g. a second open-source engine. */
  engine2?: number
  pandaCoreName: string
  singBoxName: string
  engine2Name?: string
  rateUnit?: string
  periodSeconds?: number
  /** Screen-reader summary of what the animation shows. */
  description: string
}>(), {
  engine2: 0,
  engine2Name: '',
  rateUnit: 'Gbit/s',
  periodSeconds: 10,
})

const HOLD_SECONDS = 4
const { t } = useI18n()
const LABEL_HEIGHT = 30
const TRACK_HEIGHT = 14
const rootRef = ref<HTMLElement>()
const stageRef = ref<HTMLElement>()
const canvasRef = ref<HTMLCanvasElement>()
const playheadRef = ref<HTMLElement>()
const colorMode = useColorMode()

const elapsed = ref(0)
const holding = ref(false)
const stagePixels = ref(0)
const reducedMotion = ref(false)
const visible = ref(false)
const pageVisible = ref(true)

interface Particle {
  x: number
  len: number
  dy: number
  alpha: number
}

type LaneId = 'panda' | 'sing' | 'engine2'

interface Lane {
  id: LaneId
  rate: number
  particles: Particle[]
  spawnAccumulator: number
}

const lanes: Lane[] = [
  { id: 'panda', rate: props.pandaCore, particles: [], spawnAccumulator: 0 },
  { id: 'sing', rate: props.singBox, particles: [], spawnAccumulator: 0 },
  ...(props.engine2 > 0 ? [{ id: 'engine2' as const, rate: props.engine2, particles: [], spawnAccumulator: 0 }] : []),
]

const laneNames = computed<Record<LaneId, string>>(() => ({ panda: props.pandaCoreName, sing: props.singBoxName, engine2: props.engine2Name }))
/** Two lanes keep the original 10.5rem stage; every extra lane adds the same per-lane height. */
const stageHeight = `${lanes.length * 4.9 + 0.7}rem`

const maxRate = Math.max(...lanes.map(lane => lane.rate))
if (!(maxRate > 0)) {
  throw new Error('Throughput race needs a positive rate')
}

const isStatic = computed(() => reducedMotion.value)
const clampedElapsed = computed(() => Math.min(props.periodSeconds, elapsed.value))
const totalMax = gigabytesTransferred(maxRate, props.periodSeconds)

const ticks = computed(() => {
  const step = props.periodSeconds >= 10 ? 5 : 1
  const list: number[] = []
  for (let value = 0; value <= props.periodSeconds; value += step) {
    list.push(value)
  }
  return list
})

const gbFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const rateFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const laneViews = computed(() => {
  const seconds = isStatic.value ? props.periodSeconds : clampedElapsed.value
  const laneHeight = stagePixels.value / lanes.length
  return lanes.map((lane, index) => ({
    id: lane.id,
    name: laneNames.value[lane.id],
    rateText: `${rateFormatter.format(lane.rate)} ${props.rateUnit}`,
    gbText: gbFormatter.format(gigabytesTransferred(lane.rate, seconds)),
    fillRatio: lane.rate / maxRate,
    multiplierText: lane.id === 'panda' ? null : verdictFromMedians(props.pandaCore, lane.rate, true) === 'tie' ? t('pandacore.throughput.verdict_tie') : `${props.pandaCoreName} ${formatMultiplier(props.pandaCore / lane.rate)}`,
    centerY: index * laneHeight + LABEL_HEIGHT + (laneHeight - LABEL_HEIGHT) / 2,
  }))
})

interface Palette {
  track: string
  fill: Record<LaneId, string>
  particle: Record<LaneId, string>
  headPanda: string
}

let palette: Palette | null = null
let ctx: CanvasRenderingContext2D | null = null
let width = 0
let height = 0
let frame: number | null = null
let lastFrameAt = 0
let simulatedSeconds = 0
let resetFade = 0
let resizeObserver: ResizeObserver | null = null
let intersectionObserver: IntersectionObserver | null = null
let motionQuery: MediaQueryList | null = null
let lastCounterSync = 0

function readPalette(): Palette {
  const style = getComputedStyle(rootRef.value!)
  const read = (name: string) => {
    const value = style.getPropertyValue(name).trim()
    if (!value) {
      throw new Error(`Throughput race is missing CSS variable ${name}`)
    }
    return value
  }
  return {
    track: read('--race-track'),
    fill: { panda: read('--race-fill-panda'), sing: read('--race-fill-sing'), engine2: read('--race-fill-engine2') },
    particle: { panda: read('--race-particle-panda'), sing: read('--race-particle-sing'), engine2: read('--race-particle-engine2') },
    headPanda: read('--race-head-panda'),
  }
}

function laneCenterY(index: number) {
  const laneHeight = height / lanes.length
  return index * laneHeight + LABEL_HEIGHT + (laneHeight - LABEL_HEIGHT) / 2
}

function roundedRect(x: number, y: number, w: number, h: number, radius: number) {
  if (!ctx) {
    return
  }
  const r = Math.min(radius, h / 2, Math.max(w, 0) / 2)
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.arcTo(x + w, y, x + w, y + r, r)
  ctx.lineTo(x + w, y + h - r)
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r)
  ctx.lineTo(x + r, y + h)
  ctx.arcTo(x, y + h, x, y + h - r, r)
  ctx.lineTo(x, y + r)
  ctx.arcTo(x, y, x + r, y, r)
  ctx.closePath()
}

function draw(seconds: number, fadeAlpha: number, withParticles: boolean) {
  if (!ctx || !palette || width === 0) {
    return
  }
  ctx.clearRect(0, 0, width, height)
  lanes.forEach((lane, index) => {
    const y = laneCenterY(index)
    const top = y - TRACK_HEIGHT / 2

    ctx!.fillStyle = palette!.track
    roundedRect(0, top, width, TRACK_HEIGHT, 6)
    ctx!.fill()

    const transferred = gigabytesTransferred(lane.rate, seconds)
    const fillWidth = Math.min(width, (transferred / totalMax) * width)
    if (fillWidth > 0) {
      ctx!.globalAlpha = fadeAlpha
      ctx!.fillStyle = palette!.fill[lane.id]
      roundedRect(0, top, fillWidth, TRACK_HEIGHT, 6)
      ctx!.fill()
      if (lane.id === 'panda' && fillWidth > 8) {
        ctx!.fillStyle = palette!.headPanda
        roundedRect(fillWidth - 3, top - 2, 3, TRACK_HEIGHT + 4, 1.5)
        ctx!.fill()
      }
      ctx!.globalAlpha = 1
    }

    if (withParticles) {
      const color = palette!.particle[lane.id]
      for (const particle of lane.particles) {
        ctx!.globalAlpha = particle.alpha
        ctx!.fillStyle = color
        roundedRect(particle.x, y + particle.dy - 1.5, particle.len, 3, 1.5)
        ctx!.fill()
      }
      ctx!.globalAlpha = 1
    }
  })
}

function drawStatic() {
  draw(props.periodSeconds, 1, false)
  movePlayhead(props.periodSeconds)
}

/**
 * The playhead is moved straight on the DOM every frame. Routing it through Vue state
 * would tie it to the throttled counter updates and make it visibly step.
 */
function movePlayhead(seconds: number) {
  const playhead = playheadRef.value
  if (!playhead) {
    return
  }
  const ratio = Math.min(1, Math.max(0, seconds / props.periodSeconds))
  playhead.style.transform = `translateX(${ratio * width}px)`
}

function step(now: number) {
  frame = null
  if (!ctx) {
    return
  }
  const dt = lastFrameAt === 0 ? 0 : Math.min(0.05, (now - lastFrameAt) / 1000)
  lastFrameAt = now

  if (resetFade > 0) {
    resetFade = Math.max(0, resetFade - dt / 0.45)
  }
  else {
    simulatedSeconds += dt
    if (simulatedSeconds >= props.periodSeconds + HOLD_SECONDS) {
      simulatedSeconds = 0
      resetFade = 1
      holding.value = false
      for (const lane of lanes) {
        lane.particles.length = 0
      }
    }
    else if (simulatedSeconds >= props.periodSeconds && !holding.value) {
      // Hold at the end: the packet stream stops and only the multipliers stay in the empty track.
      holding.value = true
      for (const lane of lanes) {
        lane.particles.length = 0
      }
    }
  }

  const shownSeconds = resetFade > 0 ? props.periodSeconds : Math.min(props.periodSeconds, simulatedSeconds)
  const fadeAlpha = resetFade > 0 ? resetFade : 1

  // Packets arrive from the network on the right and travel left; the moment one reaches
  // the head of the transferred-volume fill it is absorbed into the solid bar.
  const speed = width / 1.35
  for (const lane of lanes) {
    if (resetFade === 0 && !holding.value) {
      const perSecond = 4 + 34 * (lane.rate / maxRate)
      lane.spawnAccumulator += perSecond * dt
      while (lane.spawnAccumulator >= 1) {
        lane.spawnAccumulator -= 1
        lane.particles.push({
          x: width + 4,
          len: 10 + Math.random() * 18,
          dy: (Math.random() - 0.5) * 7,
          alpha: 0.45 + Math.random() * 0.55,
        })
      }
    }
    const fillWidth = Math.min(width, (gigabytesTransferred(lane.rate, shownSeconds) / totalMax) * width)
    for (const particle of lane.particles) {
      particle.x -= speed * dt
    }
    lane.particles = lane.particles.filter(particle => particle.x > fillWidth && particle.x + particle.len > -4)
  }

  draw(shownSeconds, fadeAlpha, true)
  movePlayhead(shownSeconds)

  if (now - lastCounterSync > 70) {
    lastCounterSync = now
    elapsed.value = resetFade > 0 ? props.periodSeconds : simulatedSeconds
  }

  if (shouldRun()) {
    frame = requestAnimationFrame(step)
  }
}

function shouldRun() {
  return visible.value && pageVisible.value && !reducedMotion.value
}

function syncLoop() {
  if (shouldRun()) {
    if (frame === null) {
      lastFrameAt = 0
      frame = requestAnimationFrame(step)
    }
  }
  else if (frame !== null) {
    cancelAnimationFrame(frame)
    frame = null
  }
}

function resize() {
  const stage = stageRef.value
  const canvas = canvasRef.value
  if (!stage || !canvas) {
    return
  }
  const rect = stage.getBoundingClientRect()
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  width = rect.width
  height = rect.height
  stagePixels.value = rect.height
  canvas.width = Math.round(width * dpr)
  canvas.height = Math.round(height * dpr)
  ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable')
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  if (reducedMotion.value) {
    holding.value = true
    drawStatic()
  }
  else if (frame === null) {
    const seconds = Math.min(props.periodSeconds, simulatedSeconds)
    draw(seconds, 1, true)
    movePlayhead(seconds)
  }
}

function handleVisibilityChange() {
  pageVisible.value = document.visibilityState === 'visible'
  syncLoop()
}

function handleMotionChange(event: MediaQueryListEvent) {
  reducedMotion.value = event.matches
  holding.value = event.matches
  if (event.matches) {
    drawStatic()
  }
  else {
    simulatedSeconds = 0
    elapsed.value = 0
  }
  syncLoop()
}

onMounted(() => {
  motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  reducedMotion.value = motionQuery.matches
  motionQuery.addEventListener('change', handleMotionChange)
  palette = readPalette()
  resize()

  resizeObserver = new ResizeObserver(() => resize())
  resizeObserver.observe(stageRef.value!)

  intersectionObserver = new IntersectionObserver((entries) => {
    visible.value = entries.some(entry => entry.isIntersecting)
    syncLoop()
  }, { threshold: 0.15 })
  intersectionObserver.observe(rootRef.value!)

  document.addEventListener('visibilitychange', handleVisibilityChange)
})

watch(() => colorMode.value, () => {
  nextTick(() => {
    palette = readPalette()
    if (reducedMotion.value) {
      drawStatic()
    }
  })
})

onBeforeUnmount(() => {
  if (frame !== null) {
    cancelAnimationFrame(frame)
  }
  resizeObserver?.disconnect()
  intersectionObserver?.disconnect()
  motionQuery?.removeEventListener('change', handleMotionChange)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<style scoped>
.race {
  --race-track: rgba(255, 255, 255, 0.06);
  --race-fill-panda: rgba(126, 211, 33, 0.55);
  --race-fill-sing: rgba(139, 148, 136, 0.42);
  --race-fill-engine2: rgba(201, 168, 92, 0.4);
  --race-particle-panda: #d4ff9a;
  --race-particle-sing: #c9d0c5;
  --race-particle-engine2: #ead9a6;
  --race-head-panda: #e9ffc4;
  display: grid;
  gap: 0.35rem;
  width: 100%;
}

.light .race {
  --race-track: rgba(51, 65, 85, 0.08);
  --race-fill-panda: rgba(45, 90, 39, 0.62);
  --race-fill-sing: rgba(100, 116, 139, 0.36);
  --race-fill-engine2: rgba(154, 122, 31, 0.38);
  --race-particle-panda: #2d5a27;
  --race-particle-sing: #64748b;
  --race-particle-engine2: #9a7a1f;
  --race-head-panda: #1f4a1a;
}

.race-stage {
  position: relative;
}

.race-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.race-lane {
  position: absolute;
  left: 0;
  right: 0;
  height: 30px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  pointer-events: none;
}

.race-lane-head {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
}

.race-lane-name {
  color: var(--pc-text, #f5f7f1);
  font-size: 0.86rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.race-lane.is-sing .race-lane-name,
.race-lane.is-engine2 .race-lane-name {
  color: var(--pc-copy, #969f92);
}

.race-lane-rate {
  color: var(--pc-faint, #5f675c);
  font-size: 0.74rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.race-lane-total {
  display: flex;
  align-items: baseline;
  gap: 0.28rem;
  font-family: Metropolis, 'SF Pro Display', 'Segoe UI', sans-serif;
  font-variant-numeric: tabular-nums;
}

.race-lane-gb {
  color: var(--pc-text, #f5f7f1);
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
}

.race-lane.is-sing .race-lane-gb,
.race-lane.is-engine2 .race-lane-gb {
  color: var(--pc-copy, #969f92);
}

.race-lane-unit {
  color: var(--pc-faint, #5f675c);
  font-size: 0.72rem;
  font-weight: 700;
}

.race-lane-mult {
  position: absolute;
  display: inline-flex;
  align-items: baseline;
  gap: 0.3rem;
  transform: translateY(-50%);
  color: var(--pc-faint, #5f675c);
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.35s ease;
}

.race-lane-mult strong {
  color: var(--pc-green, #7ed321);
  font-family: Metropolis, 'SF Pro Display', 'Segoe UI', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.race-lane-mult.is-shown {
  opacity: 1;
}

.race-axis {
  position: relative;
  height: 1.4rem;
  border-top: 1px solid var(--pc-line, rgba(255, 255, 255, 0.09));
}

.race-tick {
  position: absolute;
  top: 0.4rem;
  transform: translateX(-50%);
  color: var(--pc-faint, #5f675c);
  font-size: 0.68rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.race-tick:first-child {
  transform: none;
}

.race-tick:last-of-type {
  transform: translateX(-100%);
}

.race-playhead {
  position: absolute;
  top: -1px;
  left: 0;
  width: 1px;
  height: 0.5rem;
  background: var(--pc-green, #7ed321);
  will-change: transform;
}

.race.is-static .race-playhead {
  display: none;
}
</style>
